import {
  QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { Fragment, useState } from "react";
import Button from "../../../form/Button";
import Form from "../../../form/Form";
import useForm from "../../../../hooks/useForm";
import { categoryAddEdit } from "../../../../services/categoryService";
import { toast } from "react-toastify";
import Input from "../../../form/Input";
import Textarea from "../../../form/Textarea";
import { lngDetect } from "../../../../helpers/lngDetect";
import Tabs from "../../../elements/Tabs";

const FormComponent = ({
  getResponse = () => {},
  close = () => {},
  data,
  lang,
}) => {
  const [errors, setErrors] = useState({});
  const [editList, setEditList] = useState(
    JSON.parse(JSON.stringify(data?.ancestorsGraph || []))
  );

  const handleDeleteNestedItem = (path) => {
    const updated = JSON.parse(JSON.stringify(editList));
    let ref = updated;

    for (let i = 0; i < path.length - 1; i++) {
      ref = ref[path[i]].children;
    }

    ref.splice(path[path.length - 1], 1);
    setEditList(updated);
  };

  const renderNestedEditor = (items, level = 0, path = []) => {
    return items.map((item, index) => {
      const currentPath = [...path, index];
      const key = currentPath.join(".");
      return (
        <div
          key={key}
          style={{ marginLeft: `${level * 16}px` }}
          className="mb-3"
        >
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <Input
                label={"Subcategory Name"}
                inputClasses="border px-2 py-1 rounded w-full text-sm mb-1"
                value={lngDetect(item.name) || ""}
                onChange={(e) =>
                  handleNestedChange(currentPath, "name", e.target.value)
                }
                required
              />
              {errors[key] && (
                <div className="text-xs text-red-600 mb-1">
                  <i className="fa-regular fa-fw fa-square-exclamation text-sm mr-1"></i>
                  {errors[key]}
                </div>
              )}
            </div>
            <Button
              type="button"
              buttonClasses="mt-4"
              buttonIcon="fa-solid fa-trash"
              buttonFunction={() => handleDeleteNestedItem(currentPath)}
            />
          </div>
          {item.ancestors &&
            renderNestedEditor(item.ancestors, level + 1, currentPath)}
        </div>
      );
    });
  };

  const handleNestedChange = (path, field, value) => {
    console.log(value);

    const updated = JSON.parse(JSON.stringify(editList));
    let ref = updated;
    for (let i = 0; i < path.length - 1; i++) {
      ref = ref[path[i]].ancestors;
    }
    ref[path[path.length - 1]][field][lang] = value;
    setEditList(updated);
  };

  const { mutate: onSubmit, isPending: isLoading } = useMutation({
    mutationFn: async (val) => {
      return await categoryAddEdit(val);
    },
    onSuccess: (res) => {
      if (res?.status === 200 || res?.status === 201) {
        toast.success(res?.message);
        close();
        getResponse(res);
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      toast.error("Something went wrong!");
      console.error(error);
    },
  });

  const handleFinalSubmit = () => {
    const validationErrors = {};

    const validateItems = (items, path = []) => {
      items.forEach((item, index) => {
        const currentPath = [...path, index];
        const key = currentPath.join(".");

        const name = item?.name?.[lang]?.trim();
        if (!name) {
          validationErrors[key] = "Name is required";
        }

        if (item.children && item.children.length > 0) {
          validateItems(item.children, currentPath);
        }
      });
    };

    validateItems(editList);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({}); // clear previous errors

    const { media, ...rest } = data;
    const payload = {
      ...rest,
      type: "event-category",
      image: data?.media?.image?.url,
      ancestorsGraph: editList,
      ...(data?._id && { id: data?._id }),
    };
    console.log({ payload });
    return;

    onSubmit(payload);
  };

  return (
    <Fragment>
      <Form
        handleSubmit={(e, onSubmit) => {
          e.preventDefault(); // ✅ Prevent reload
          onSubmit(); // ✅ Call submit logic
        }}
        onSubmit={handleFinalSubmit}
      >
        {renderNestedEditor(editList)}
        <div className="relative col-span-4 mt-5">
          <div className="flex items-center justify-center gap-4">
            <Button
              buttonType="submit"
              buttonLabel={"Save Category"}
              buttonClasses={
                "!rounded-full !px-8 !h-12 !border !border-primary !bg-transparent !text-primary hover:!bg-primary hover:!text-white"
              }
              buttonLabelClasses={"mx-auto"}
              {...(isLoading && {
                loading: true,
                buttonIconPosition: "left",
                isDisable: true,
              })}
            />
            <Button
              buttonLabel={"Cancel"}
              buttonClasses={
                "!rounded-full !px-8 !h-12 !border !border-primary !bg-transparent !text-primary hover:!bg-primary hover:!text-white"
              }
              buttonLabelClasses={"mx-auto"}
              buttonFunction={close}
            />
          </div>
        </div>
      </Form>
    </Fragment>
  );
};

const ManageSubCategory = ({
  getResponse = () => {},
  close = () => {},
  data,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const tabs = [
    {
      label: "English",
      content: (
        <FormComponent
          getResponse={getResponse}
          close={close}
          data={data}
          lang={"en"}
        />
      ),
    },
    {
      label: "Spanish",
      content: (
        <FormComponent
          getResponse={getResponse}
          close={close}
          data={data}
          lang={"es"}
        />
      ),
    },
  ];

  return (
    <div className="h-full p-4">
      <Tabs
        items={tabs}
        setSelectedIndex={setSelectedIndex}
        selectedIndex={selectedIndex}
      />
    </div>
  );
};

export default ManageSubCategory;
